export const instanceOfTypes = new Map([
  ['Date', {
    isEqual: isEqualDate,
    copy: copyDate,
  }],
  ['RegExp', {
    isEqual: isEqualRegExp,
    copy: copyRegExp,
  }],
  ['Array', {
    isEqual: isEqualArray,
    copy: copyArray,
  }],
  ['Map', {
    isEqual: isEqualMap,
    copy: copyMap,
  }],
  ['WeakMap', { // Use shallow copy & equality check due to WeakMap's nature
    isEqual: (a, b) => a === b,
    copy: (map) => map,
  }],
  ['Set', {
    isEqual: isEqualSet,
    copy: copySet,
  }],
  ['Function', {
    isEqual: (a, b) => a.toString() === b.toString(),
    copy: (func) => func,
  }],
  ['Object', {
    isEqual: isEqualObject,
    copy: copyObject,
  }],
]);

/**
 * Recursively make a deep copy of the provided value.
 */
export function deepCopy (value) {
  if (
    (usesShallowComparison(value))
    || (Number.isNaN(value))
  ) { return value }

  const type = getType(value);
  if (!type) {
    throw new Error(typeErrorMessage(value));
  }

  const { copy } = instanceOfTypes.get(type);

  return copy(value);
}

/**
 * Recursively compare a & b for equality. Order is ignored.
 */
export function isDeepEqual (a, b) {
  if (
    (a === b)
    || (Number.isNaN(a) && Number.isNaN(b))
  ) { return true }

  if (
    (usesShallowComparison(a) || usesShallowComparison(b))
    || (!isSameType(a, b))
  ) { return false }

  const type = getType(a);
  if (!type) {
    throw new Error(typeErrorMessage(a));
  }

  const { isEqual } = instanceOfTypes.get(type);

  return isEqual(a, b);
}

// Helpers
const typeErrorMessage = (value) => {
  return `Failed to get type for: ${value}`;
};

export function getType (value) {
  const type = Object.prototype.toString.call(value)
    .split(' ')[1] // [object <TYPE>]
    .replace(']', '');

  return type;
}

export function isSameType (a, b) {
  return getType(a) === getType(b);
}

export function usesShallowComparison (value) {
  if (Number.isNaN(value)) {
    return false;
  }

  return (
    (value === null)
    || [
      'boolean',
      'string',
      'number',
      'bigint',
      'undefined',
      'symbol',
      // 'function'
    ].includes(typeof value)
  );
}

// Equality check & copy functions
function isEqualDate (a, b) {
  return a.valueOf() === b.valueOf();
}

function copyDate (date) {
  return new Date(date.valueOf());
}

function isEqualRegExp (a, b) {
  return `${a.source}/${a.flags}` === `${b.source}/${b.flags}`;
}

function copyRegExp (regexp) {
  return new RegExp(regexp.source, regexp.flags);
}

function isEqualArray (a, b) {
  if (a.length !== b.length) {
    return false;
  }

  const skipIndexes = new Map();

  return a.every((itemA) => b.some((itemB, idx) => {
    if (!skipIndexes.has(idx) && isDeepEqual(itemA, itemB)) {
      skipIndexes.set(idx, true);

      return true;
    }

    return false;
  }));
}

function copyArray (arr) {
  return arr.map((item) => deepCopy(item));
}

function isEqualMap (a, b) {
  if (a.size !== b.size) {
    return false;
  }

  return isDeepEqual(
    Array.from(a.entries()),
    Array.from(b.entries()),
  );
}

function copyMap (map) {
  return new Map(deepCopy(Array.from(map.entries())));
}

function isEqualSet (a, b) {
  if (a.size !== b.size) {
    return false;
  }

  return isDeepEqual(
    Array.from(a.values()),
    Array.from(b.values()),
  );
}

function copySet (set) {
  return new Set(deepCopy(Array.from(set)));
}

function isEqualObject (a, b) {
  const [entriesA, entriesB] = [Object.entries(a), Object.entries(b)];
  if (entriesA.length !== entriesB.length) {
    return false;
  }

  return isDeepEqual(entriesA, entriesB);
}

function copyObject (obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, deepCopy(value)]),
  );
}
